import { useCallback, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '../lib/utils.js';
import { disabledFade, insetFocusRing } from '../lib/focus.js';
import { isRtl } from '../lib/rtl.js';

/* ===========================================================================
 * Segmented — the range / scope / view switcher, and the toolbar's third slot.
 *
 * WHAT IT IS. A recessed track holding two to five mutually exclusive choices,
 * the current one drawn as a raised pale pill. It appears four times in the
 * handoff and means something slightly different each time — "Today / Week /
 * Month" on the Overview, "All / Site workers / Staff" on Employees, "Pending /
 * Approved / Rejected" on Approvals, the five table views on Reports — but it is
 * one control, and that is the point: a reader learns the shape once.
 *
 * ---------------------------------------------------------------------------
 * THE KEYBOARD CONTRACT, WHICH IS PINNED BY A TEST ON A SCREEN THIS COMPONENT
 * DOES NOT YET RENDER.
 *
 * `apps/web/src/routes/reports/workspace/ReportsWorkspace.views.test.tsx`
 * asserts four things about the reports view strip, and they are assertions
 * about a DESIGN DECISION rather than an implementation detail, so they survive
 * the adoption rather than being updated by it:
 *
 *   1. it is a real `tablist` of `tab`s, so a screen reader says "3 of 5"
 *      instead of reading five unrelated buttons;
 *   2. ONE tab stop for the whole strip — only the selected segment is reachable
 *      with Tab, the rest with the arrow keys (a roving `tabindex`). Five stops
 *      on the way to a table is five stops nobody wanted;
 *   3. ArrowRight/ArrowLeft walk it, wrapping at both ends, and Home/End jump to
 *      the first and last segment;
 *   4. **the arrows walk the other way in an RTL page.** The key points at a
 *      direction on screen, not at an array index — in Arabic the strip is drawn
 *      right-to-left, so ArrowRight moves towards its START.
 *
 * WHAT WAS REUSED. `Tabs.tsx` already has the roving-focus machinery and this
 * component is built from the same parts: a ref per trigger, the roving
 * `tabIndex={selected ? 0 : -1}`, the key handler on the TRIGGER rather than the
 * list (so a key pressed on a segment is handled by the segment that has
 * focus), and the wrap-around modulo. Direction resolution (`isRtl`, shared via
 * `../lib/rtl.js`) and the Home-is-first/End-is-last semantics of requirement 4
 * above now live in both components — `Tabs.tsx` carried the same two bugs this
 * header used to document as its own until they were ported over from here.
 *
 * `Tabs` also carries a registry that sorts its triggers by
 * `compareDocumentPosition`, because `Tabs.Trigger` children are arbitrary JSX
 * whose DOM order the parent cannot know. Here the order IS the `items` array,
 * so a plain ref array indexed by position is the same mechanism with the
 * indirection removed.
 *
 * ---------------------------------------------------------------------------
 * ACTIVATION — manual by default, and that is not the ARIA default.
 *
 * The tabs pattern normally lets selection follow focus, which is right when the
 * panel is cheap. The reports strip is the opposite: selecting a view rewrites
 * the URL and re-columns a table of up to 2,000 rows, so arrowing across five
 * views would run five of those. `manual` moves focus only; Enter or Space
 * commits. `automatic` is there for a strip whose panel is a filter over data
 * already on screen.
 *
 * A `radiogroup` gets `automatic` by default instead, because a radio's
 * selection follows focus by definition.
 *
 * ---------------------------------------------------------------------------
 * ROLE. `tablist` is the default and is what the reports test pins. It is the
 * wrong role for three of the four places the handoff uses this control: a
 * range switcher does not reveal a tabpanel, it filters the page, and claiming
 * `tablist` there promises a panel relationship that does not exist. So
 * `role="radiogroup"` is offered, with identical geometry and an identical
 * keyboard contract — only the announced semantics change. One control, two
 * honest readings, rather than two controls.
 *
 * CONTROLLED ONLY. There is no `defaultValue`. Every real instance of this
 * derives its value from somewhere that already owns it — the URL on Reports, a
 * filter store on Employees — and an internal copy would be a second source of
 * truth for which segment is lit.
 *
 * RTL: no physical direction anywhere. The track is a flex row on the inline
 * axis, so it mirrors with `dir` on its own; the only direction-aware code is
 * the arrow-key mapping described above.
 * ======================================================================== */

export interface SegmentedItem {
  /** The value this segment selects. Also its React key. */
  value: string;
  /** What the segment says. Sentence case — never 11px caps (rules §1). */
  label: ReactNode;
  /**
   * The accessible name, when `label` is not a plain string (an icon, a label
   * with a count beside it). Falls back to `label`'s own text content.
   */
  ariaLabel?: string;
  /**
   * Unavailable. A disabled segment does NOT animate — motion promises
   * something will happen (rules §1). Prefer leaving a choice out over
   * disabling it: a segment that cannot be picked and does not say why is the
   * worst state in the product (rules §3.11).
   */
  disabled?: boolean;
}

export interface SegmentedProps {
  /**
   * The strip's accessible name — "Date range", "Workforce group", "Table
   * view". Required: a bare row of five words is not self-describing, and this
   * is the control's only label since it has no visible one.
   */
  label: string;
  items: readonly SegmentedItem[];
  /** The selected value. Controlled — see the header note. */
  value: string;
  onValueChange: (value: string) => void;
  /**
   * The `id` of the region this strip re-renders, when there is exactly one.
   * Emitted as `aria-controls` on every segment. Omitted rather than guessed:
   * an `aria-controls` pointing at an id that is not in the document is worse
   * than none.
   */
  controls?: string;
  /**
   * `tablist` (default) or `radiogroup`. See the header note — the geometry and
   * the keyboard contract are identical; only what is announced changes.
   */
  role?: 'tablist' | 'radiogroup';
  /**
   * `manual` — arrows move focus, Enter/Space commits. `automatic` — selection
   * follows focus. Defaults to `manual` for a `tablist` and `automatic` for a
   * `radiogroup`; see the header note for why those are the right defaults and
   * not the same one.
   */
  activation?: 'manual' | 'automatic';
  /**
   * `track` (default) — the recessed strip with a raised pale pill, which is
   * what the handoff draws for a toolbar's scope filter (Reports, Approvals,
   * the device register).
   *
   * `pills` — no track at all: a bare row where the chosen item is a
   * BRAND-FILLED pill and the rest are quiet text. The handoff uses this, and
   * only this, for the one control that switches which SCREEN you are looking
   * at — Operations' People / Sites / Camps
   * (`Hadir Operations.dc.html:219-222`, 34px tall, `0 14px`, radius 10, and a
   * brand fill with a coloured shadow on the active one).
   *
   * Two variants rather than one compromise, because the two are saying
   * different things: a track means "these are settings of the view you are
   * on", a filled pill means "this IS the view". Collapsing them would make
   * the reports toolbar shout or the view switcher whisper.
   */
  variant?: 'track' | 'pills';
  className?: string;
}


/**
 * The recessed track. 3px of padding is what makes the pill look inset.
 *
 * ---------------------------------------------------------------------------
 * `max-w-full flex-wrap` — WHY A STRIP IS ALLOWED TO TAKE A SECOND LINE.
 *
 * This was `inline-flex flex-none … ` with `whitespace-nowrap` on every
 * segment, i.e. a box that could neither shrink nor break. That is right while
 * there is room and wrong the moment there is not, and the failure was not
 * subtle: it pushed the page sideways.
 *
 * The chain. `ControlBar` puts each slot in a group that deliberately has NO
 * `min-w-0`, so a cramped bar re-flows onto more lines rather than squashing a
 * field — but a flex item's automatic minimum size is its MIN-CONTENT width,
 * and the min-content width of a track that cannot wrap is the whole strip. So
 * the group could not shrink below it, the bar does not clip, and the strip
 * was painted straight out through the toolbar's rounded border. Because
 * `AppShell`'s content column is `overflow-y-auto` — which computes `overflow-x`
 * to `auto` as well — the whole page then scrolled sideways, taking the sticky
 * toolbar band off the screen with it. Measured in Chrome against these exact
 * classes: the five-segment device-approvals strip is 407px and the
 * five-segment reports strip is 426px, and in the 335px bar of a 375px phone
 * they hung 78px and 98px past the bar's edge.
 *
 * `flex-wrap` drops the track's min-content width to its WIDEST SEGMENT, which
 * is what lets the group shrink; `max-w-full` is what then clamps the track to
 * the space the group was given, so it wraps instead of overflowing. Nothing
 * changes while the strip fits — same 407px, same single 38px row — because
 * `flex-none` still bases it on max-content.
 *
 * WRAPPING RATHER THAN SCROLLING was the choice. `overflow-x-auto` on the track
 * would also have released the minimum, and it is the familiar phone pattern,
 * but a segmented strip in this app is a SCOPE FILTER: hiding "Revoked" past a
 * scroll edge means a reader who has never seen it does not know the view
 * exists. It also makes the track a scroll container in BOTH axes, which clips
 * the selected pill's `shadow-resting`. Taking a second line keeps every choice
 * visible and is the same answer `ControlBar` gives one level up.
 */
const TRACK_CLASS =
  'inline-flex max-w-full flex-none flex-wrap items-center gap-0.5 rounded-control bg-surface-2 p-[3px] min-h-control';

/**
 * The `pills` row — no track, no inset, and 4px between items rather than 2px
 * (`Hadir Operations.dc.html:219`). It keeps `max-w-full flex-wrap` for exactly
 * the reason the track does: a row that cannot wrap sets its own min-content
 * width as the group's floor and pushes the page sideways on a phone. See the
 * long note above; the failure mode is identical here.
 */
const PILLS_CLASS = 'inline-flex max-w-full flex-none flex-wrap items-center gap-1';

/**
 * One segment. `insetFocusRing` rather than the outset `focusRing`: the track
 * holds each segment 3px from its own edge, so an outset ring would be drawn
 * over the track's shoulder and — inside a `ControlBar` that clips — partly cut
 * off. Same width and colour, pulled inwards.
 */
const SEGMENT_CLASS = cn(
  'inline-flex items-center justify-center whitespace-nowrap',
  // `text-copy font-semibold` — 13.5px at 600, which is what all four strips in
  // the handoff set (`min-height: 32px; padding: 0 12px; border-radius: 8px;
  // font-size: 13.5px; font-weight: 600`, Hadir Reports.dc.html:361-365). It was
  // `text-label`, whose 600 is right and whose 12.5px is a step small: a segment
  // holds a WORD a reader chooses between, not a field label, so it sits at the
  // body step like the buttons either side of it in the toolbar. The weight has
  // to be said separately because `text-copy` is the 400 body step.
  'text-copy font-semibold',
  '[@media(pointer:coarse)]:min-h-touch',
  'transition-[background-color,box-shadow,color] duration-fast ease-standard',
  // Geometry moved to the per-variant classes below — a tracked segment is the
  // handoff's 32px/12px/8px, a bare pill is its 34px/14px/10px.
  insetFocusRing,
  // A DISABLED CONTROL NEVER ANIMATES (rules §1). `transition-none` is the
  // half of that no token can carry — `disabled:` variants can only turn a
  // transition off, and this is the only place that can be said.
  cn('disabled:pointer-events-none disabled:transition-none', disabledFade)
);

/** Tracked segment geometry — the handoff's 32px / `0 12px` / radius 8. */
const SEGMENT_TRACK_GEOMETRY = 'min-h-8 rounded-xs px-3';

/**
 * Bare-pill geometry — the handoff's 34px / `0 14px` / radius 10
 * (`Hadir Operations.dc.html:221`). `rounded-control` is the 10px role, so no
 * arbitrary value is needed.
 */
const SEGMENT_PILL_GEOMETRY = 'min-h-pill rounded-control px-3.5';

export function Segmented({
  label,
  items,
  value,
  onValueChange,
  controls,
  role = 'tablist',
  activation,
  variant = 'track',
  className,
}: SegmentedProps): JSX.Element {
  const pills = variant === 'pills';
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const isTablist = role === 'tablist';
  const follows = (activation ?? (isTablist ? 'manual' : 'automatic')) === 'automatic';

  const move = useCallback(
    (from: number, key: string, node: HTMLButtonElement): void => {
      const count = items.length;
      if (count === 0) return;

      // A DISABLED SEGMENT IS SKIPPED, not landed on. A `<button disabled>` is
      // not focusable at all, so "include it in the walk" is not an option the
      // platform offers — calling `focus()` on one is a silent no-op that would
      // leave the arrow key looking broken. Walking past it keeps every enabled
      // choice reachable, which is what the key is for.
      const forward =
        key === 'Home' ? true : key === 'End' ? false : key === (isRtl(node) ? 'ArrowLeft' : 'ArrowRight');
      const step = forward ? 1 : -1;
      // Home/End start from OUTSIDE the strip and step inwards, so they land on
      // the first/last ENABLED segment rather than on a disabled end cap.
      const start = key === 'Home' ? -1 : key === 'End' ? count : from;

      let next = start;
      for (let hops = 0; hops < count; hops += 1) {
        next = (next + step + count) % count;
        const candidate = items[next];
        const target = refs.current[next];
        if (!candidate || candidate.disabled || !target) continue;
        target.focus();
        // `automatic` commits on arrival; `manual` leaves the value alone until
        // Enter or Space commits it (see the header note on activation).
        if (follows) onValueChange(candidate.value);
        return;
      }
    },
    [follows, items, onValueChange]
  );

  function handleKey(e: KeyboardEvent<HTMLButtonElement>, index: number): void {
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft' || e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      move(index, e.key, e.currentTarget);
      return;
    }
    // Under `manual` activation the keys that commit are the ones a button
    // already commits on, so React's synthetic click covers them and there is
    // nothing to add here. Spelled out rather than left implicit because the
    // absence of a handler is the kind of thing a later reader "fixes".
  }

  return (
    <div
      role={role}
      aria-label={label}
      className={cn(pills ? PILLS_CLASS : TRACK_CLASS, className)}
    >
      {items.map((item, i) => {
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role={isTablist ? 'tab' : 'radio'}
            disabled={item.disabled}
            aria-label={item.ariaLabel}
            // `aria-selected` for a tab, `aria-checked` for a radio. Writing
            // both would make the control claim to be two things.
            aria-selected={isTablist ? selected : undefined}
            aria-checked={isTablist ? undefined : selected}
            aria-controls={controls}
            // Roving tabindex — one stop for the whole strip.
            //
            // It follows the SELECTED segment rather than the focused one, so
            // tabbing away and back returns to the choice in force rather than
            // to wherever an arrow key last landed. That is also what keeps the
            // strip reachable when its selected segment is one that cannot be
            // re-picked (the reports "Custom" view): the segment is rendered
            // selected and enabled, never `disabled`, because a disabled
            // selected tab leaves the strip with no focusable stop at all.
            tabIndex={selected ? 0 : -1}
            onKeyDown={(e) => handleKey(e, i)}
            onClick={() => onValueChange(item.value)}
            className={cn(
              SEGMENT_CLASS,
              pills ? SEGMENT_PILL_GEOMETRY : SEGMENT_TRACK_GEOMETRY,
              selected
                ? pills
                  ? // THE BRAND FILL, and this is the one place on a page where
                    // a segment is allowed to wear it: the handoff paints the
                    // chosen VIEW in `--primary` with its own coloured lift
                    // (`Hadir Operations.dc.html:221`). The lift is
                    // `shadow-resting` — the ROLE for a thing sitting on the
                    // page — not `shadow-brand-hover`, which §7 bans as a
                    // deprecated alias that says how big rather than what for.
                    // The mock's own lift is brand-tinted and no role carries a
                    // hue; the brand is already doing the work in the fill.
                    'bg-primary text-primary-foreground shadow-resting'
                  : // The raised pale pill. `shadow-resting` is the system's
                    // "sitting on a surface" role and is the whole reason the
                    // active segment reads as lifted out of the recessed track.
                    'bg-surface text-foreground shadow-resting'
                : pills
                  ? // No track behind it, so an unchosen pill needs a hover
                    // GROUND of its own to feel pressable — the track variant
                    // gets that from the strip it sits in.
                    'text-foreground-tertiary hover:bg-surface-2 hover:text-foreground'
                  : 'text-foreground-tertiary hover:text-foreground'
            )}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
